import { LitElement, TemplateResult, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import '../attachments-block/attachments-block';
import '../new-post/new-post';
import { Post, Thread } from '../bulletin-board.types';
import { formatWhenPosted } from '../gi-post/gi-post';
import { sortPostsByWhenPosted } from '../general-information/general-information';
import { fontAwesomeComponentStyles } from '../../variables';

@customElement('single-thread')
export class SingleThread extends LitElement {
  @property({ type: Object })
  thread: Thread | null = null;

  @property({ type: Boolean })
  currentUserCanPost = false;

  @state()
  displayReplyUi = false;

  sortedRepliesMap = new WeakMap<Array<Post>, Array<Post>>();

  static override styles = [
    fontAwesomeComponentStyles,
    css`
      :host {
        display: block;
        margin-bottom: 20px;
      }

      .wrapper {
        border: 1px solid var(--gray-300);
        border-radius: 10px;
        padding: 25px 25px 0 25px;
        background-color: var(--gray-200);
      }

      .post {
        display: flex;
        flex-direction: column;
      }

      .header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 25px;
      }

      .header-left {
        display: flex;
        align-items: center;
      }

      .postNames {
        margin-right: 20px;
      }

      .organizationName {
        font-weight: 700;
      }

      .whenPosted {
        align-self: flex-end;
        margin-bottom: 2px;

        color: var(--gray-400);
        font-size: 12px;
        font-weight: 400;
      }

      .initial-post {
        margin-bottom: 25px;
      }

      .reply {
        border-radius: 10px;

        padding: 25px;
        margin-bottom: 25px;

        background-color: white;
      }

      .btn {
        --bs-btn-padding-x: 0.75rem;
        --bs-btn-padding-y: 0.375rem;
        --bs-btn-font-family: ;
        --bs-btn-font-size: 1rem;
        --bs-btn-font-weight: 400;
        --bs-btn-line-height: 1.5;
        --bs-btn-color: var(--bs-body-color);
        --bs-btn-bg: transparent;
        --bs-btn-border-width: var(--bs-border-width);
        --bs-btn-border-color: transparent;
        --bs-btn-border-radius: var(--bs-border-radius);
        --bs-btn-hover-border-color: transparent;
        --bs-btn-box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15),
          0 1px 1px rgba(0, 0, 0, 0.075);
        --bs-btn-disabled-opacity: 0.65;
        --bs-btn-focus-box-shadow: 0 0 0 0.25rem
          rgba(var(--bs-btn-focus-shadow-rgb), 0.5);
        display: inline-block;
        padding: var(--bs-btn-padding-y) var(--bs-btn-padding-x);
        font-family: var(--bs-btn-font-family);
        font-size: var(--bs-btn-font-size);
        font-weight: var(--bs-btn-font-weight);
        line-height: var(--bs-btn-line-height);
        color: var(--bs-btn-color);
        text-align: center;
        text-decoration: none;
        vertical-align: middle;
        cursor: pointer;
        -webkit-user-select: none;
        -moz-user-select: none;
        user-select: none;
        border: var(--bs-btn-border-width) solid var(--bs-btn-border-color);
        border-radius: var(--bs-btn-border-radius);
        background-color: var(--bs-btn-bg);
        transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
          border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
      }

      .btn-group-sm > .btn,
      .btn-sm {
        --bs-btn-padding-y: 0.25rem;
        --bs-btn-padding-x: 0.5rem;
        --bs-btn-font-size: 0.875rem;
        --bs-btn-border-radius: var(--bs-border-radius-sm);
      }

      .btn-outline-light {
        --bs-btn-color: #f8f9fa;
        --bs-btn-border-color: #f8f9fa;
        --bs-btn-hover-color: #000;
        --bs-btn-hover-bg: #f8f9fa;
        --bs-btn-hover-border-color: #f8f9fa;
        --bs-btn-focus-shadow-rgb: 248, 249, 250;
        --bs-btn-active-color: #000;
        --bs-btn-active-bg: #f8f9fa;
        --bs-btn-active-border-color: #f8f9fa;
        --bs-btn-active-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.125);
        --bs-btn-disabled-color: #f8f9fa;
        --bs-btn-disabled-bg: transparent;
        --bs-btn-disabled-border-color: #f8f9;
      }

      .btn-outline-dark {
        --bs-btn-color: #212529;
        --bs-btn-border-color: #212529;
        --bs-btn-hover-color: #fff;
        --bs-btn-hover-bg: #212529;
        --bs-btn-hover-border-color: #212529;
        --bs-btn-focus-shadow-rgb: 33, 37, 41;
        --bs-btn-active-color: #fff;
        --bs-btn-active-bg: #212529;
        --bs-btn-active-border-color: #212529;
        --bs-btn-active-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.125);
        --bs-btn-disabled-color: #212529;
        --bs-btn-disabled-bg: transparent;
        --bs-btn-disabled-border-color: #212529;
        --bs-gradient: none;
      }

      [type='button']:not(:disabled) {
        cursor: pointer;
      }

      .btn:hover {
        color: var(--bs-btn-hover-color);
        background-color: var(--bs-btn-hover-bg);
        border-color: var(--bs-btn-hover-border-color);
      }

      attachments-block {
        align-self: end;
      }
    `,
  ];

  renderPost(post: Post, showReplyButton: boolean): TemplateResult {
    let replyButton;

    if (this.currentUserCanPost && showReplyButton) {
      replyButton = html`<button
        class="btn btn-outline-dark btn-sm"
        @click=${() => (this.displayReplyUi = !this.displayReplyUi)}
      >
        <i class="fa-solid fa-reply"></i> Reply
      </button>`;
    }

    return html`
      <div class="post">
        <div class="header">
          <div class="header-left">
            <div class="postNames">
              <div class="organizationName">${post.organizationName}</div>
              <div class="authorUsername">${post.authorUsername}</div>
            </div>
            <div class="whenPosted">${formatWhenPosted(post.whenPosted)}</div>
          </div>
          <div class="header-right">${replyButton}</div>
        </div>
        <div class="text">${post.text}</div>
        <attachments-block
          .attachments="${post.attachments}"
        ></attachments-block>
      </div>
    `;
  }

  renderInitialPost(post: Post): TemplateResult {
    return html`
      <div class="initial-post">${this.renderPost(post, true)}</div>
    `;
  }

  renderReplies(replies: Array<Post>): TemplateResult {
    let sortedReplies = this.sortedRepliesMap.get(replies);

    if (sortedReplies === undefined) {
      sortedReplies = [...replies].sort(sortPostsByWhenPosted);
      this.sortedRepliesMap.set(replies, sortedReplies);
    }

    return html`
      <div id="replies" class="replies">
        ${sortedReplies.map((reply: Post) => {
          return html`<div class="reply">
            ${this.renderPost(reply, false)}
          </div>`;
        })}
      </div>
    `;
  }

  renderReplyUi(): TemplateResult | void {
    if (this.displayReplyUi) {
      return html`
        <div class="reply">
          <slot></slot>
        </div>
      `;
    }
  }

  override render() {
    if (this.thread === null) {
      return html``;
    }

    // The reply UI is always at the very bottom of a thread.
    // prettier-ignore
    return html` <div class="wrapper">
      ${this.renderInitialPost(
        this.thread.initialPost
      )}
      ${this.renderReplyUi()}
      ${this.renderReplies(this.thread.replies)}
    </div>`;
  }
}
